// react imports
import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

// material-ui imports
import { TableRow } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// third-party imports
import { isEqual } from 'lodash';
import { isSystemFolder } from '..';
import { parseFileMngrDragData } from '../utils/fileMngrPath';

// ==============================|| FILEMNGR ITEM CONTAINER ||============================== //

/**
 * File Manager Item Table Row
 * @author Marcos L. A. Soto <marcosleonildo@gmail.com>
 * @param {object} props
 * @param {node}   props.children     - children
 * @param {boolean} props.isSelected  - is selected
 * @param {function} props.onClick   - on click
 * @param {function} props.onContextMenu - on context menu
 * @param {string} props.title        - title
 * @returns {node} component
 */
const FileMngrItemTableRow = ({
    children,
    isSelected,
    onClick,
    onDoubleClick,
    onContextMenu,
    title,
    isFile,
    handlePaste,
    itemData,
    setIsLoading
}) => {
    const theme = useTheme();
    const ACTIVE_BG_COLOR = '#B6DCFB60';
    const HOVERED_BG_COLOR = '#B6DCFB30';

    const [dropHasStarted, setDropHasStarted] = useState(false);

    const bgColor = useMemo(
        () => (isSelected || dropHasStarted ? ACTIVE_BG_COLOR : 'transparent'),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isSelected]
    );

    const handleLoading = (isLoading = false) => {
        setIsLoading(isLoading);
    };

    return (
        <TableRow
            title={title}
            sx={{
                bgcolor: bgColor,
                '&:hover': {
                    bgcolor: isSelected ? ACTIVE_BG_COLOR : HOVERED_BG_COLOR
                },
                cursor: 'default',
                [theme.breakpoints.down('md')]: { cursor: 'pointer' }
            }}
            onContextMenu={(e) => {
                e?.preventDefault();
                onContextMenu(e);
            }}
            onClick={(e) => {
                if (!theme.breakpoints.down('md') && !isSelected) {
                    e.stopPropagation();
                    onClick(e);
                } else {
                    onClick(e);
                }
            }}
            onDoubleClick={onDoubleClick}
            draggable={isFile === true ? 'true' : 'false'}
            onDragStart={(e) => {
                if (!isSystemFolder(itemData.name)) {
                    const data = {
                        action: 'move',
                        data: itemData,
                        names: [itemData.name],
                        path: itemData.path,
                        renameFiles: [],
                        samePage: true
                    };
                    e.dataTransfer.clearData();
                    e.dataTransfer.setData('application/json', JSON.stringify(data));
                }
            }}
            onDragOver={() => {
                setDropHasStarted(true);
            }}
            onDragLeave={() => {
                setDropHasStarted(false);
            }}
            onDrop={async (e) => {
                setDropHasStarted(false);
                const serializedData = e.dataTransfer.getData('application/json');
                if (!serializedData) {
                    return;
                }

                const draggedData = parseFileMngrDragData(serializedData);
                if (!draggedData) {
                    return;
                }

                const data = { ...draggedData, name: itemData?.name, targetData: itemData };
                //
                if (!isFile && !isEqual(data.data, itemData)) {
                    handleLoading(true);
                    await handlePaste(data);
                    handleLoading();
                }
            }}
        >
            {children}
        </TableRow>
    );
};

FileMngrItemTableRow.propTypes = {
    children: PropTypes.node,
    isSelected: PropTypes.bool,
    onClick: PropTypes.func,
    onDoubleClick: PropTypes.func,
    onContextMenu: PropTypes.func,
    title: PropTypes.string,
    isFile: PropTypes.bool,
    handlePaste: PropTypes.func,
    itemData: PropTypes.any,
    setIsLoading: PropTypes.func
};

export default FileMngrItemTableRow;
